import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function IntroduzionePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-6">
            <i className="ri-book-open-line text-orange-600"></i>
            <span className="text-orange-800 text-sm font-semibold whitespace-nowrap">Introduzione all'Opera</span>
          </div>
          <h1 className="font-serif text-6xl font-bold text-gray-900 mb-6 leading-tight">
            L'Iliade:<br />Il Poema della Collera
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            Un viaggio nel capolavoro di Omero che ha plasmato la letteratura occidentale per oltre tremila anni.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-20">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Cos'è l'Iliade?</h2>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  L'Iliade è un poema epico attribuito al poeta greco Omero, composto probabilmente nell'VIII secolo a.C. È considerato uno dei testi fondamentali della letteratura occidentale e narra gli eventi di poche settimane durante il decimo anno della guerra di Troia.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Il titolo deriva da "Ilion", nome greco di Troia. L'opera è composta da 15.693 versi in esametri dattilici, suddivisi in 24 canti (o libri). Il tema centrale è l'ira di Achille, il più grande guerriero greco, e le sue devastanti conseguenze.
                </p>
              </div>

              <div className="bg-amber-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <i className="ri-quill-pen-line text-orange-600"></i>
                  Il Primo Verso
                </h3>
                <blockquote className="font-serif text-2xl text-gray-800 italic leading-relaxed border-l-4 border-orange-600 pl-6">
                  "Canta, o dea, l'ira di Achille Pelide, rovinosa, che infiniti dolori inflisse agli Achei..."
                </blockquote>
                <p className="text-gray-600 mt-4 text-sm">
                  Con queste parole inizia il poema, invocando la Musa e annunciando il tema centrale: la collera dell'eroe.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Omero: L'Autore Leggendario</h2>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  Omero è considerato il padre della letteratura occidentale, ma la sua figura storica rimane avvolta nel mistero. Gli antichi lo descrivevano come un poeta cieco proveniente dall'Asia Minore, probabilmente dalla regione della Ionia.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  La "questione omerica" ha impegnato gli studiosi per secoli: Omero è realmente esistito? Era un singolo autore o più poeti? Le teorie moderne suggeriscono che l'Iliade sia il risultato di una lunga tradizione orale, cristallizzata in forma scritta da uno o più aedi (cantori professionisti) nell'VIII secolo a.C.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-100 rounded-xl p-6">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-mic-line text-white text-2xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Tradizione Orale</h4>
                  <p className="text-gray-700 leading-relaxed">
                    L'Iliade nasce dalla tradizione orale: i poeti tramandavano le storie di generazione in generazione, utilizzando formule metriche per facilitare la memorizzazione.
                  </p>
                </div>
                <div className="bg-gray-100 rounded-xl p-6">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-quill-pen-line text-white text-2xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Forma Scritta</h4>
                  <p className="text-gray-700 leading-relaxed">
                    La trascrizione scritta avvenne probabilmente nel VI secolo a.C. ad Atene, durante il regno di Pisistrato, che ordinò la raccolta e standardizzazione dei testi omerici.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-900 rounded-2xl p-8 text-white sticky top-24">
                <h3 className="text-2xl font-bold mb-6">Dati Essenziali</h3>
                <div className="space-y-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Autore</p>
                    <p className="text-xl font-semibold">Omero</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Periodo</p>
                    <p className="text-xl font-semibold">VIII secolo a.C.</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Lingua</p>
                    <p className="text-xl font-semibold">Greco antico</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Versi</p>
                    <p className="text-xl font-semibold">15.693</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Canti</p>
                    <p className="text-xl font-semibold">24</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Metro</p>
                    <p className="text-xl font-semibold">Esametro dattilico</p>
                  </div>
                </div>
                
                <div className="mt-8 pt-8 border-t border-white/20">
                  <Link to="/contesto-storico" className="flex items-center justify-between text-white hover:text-orange-400 transition-colors cursor-pointer">
                    <span className="font-semibold whitespace-nowrap">Contesto Storico</span>
                    <i className="ri-arrow-right-line text-xl"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Themes Section */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">I Temi Principali</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-fire-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">L'Ira e la Vendetta</h3>
                <p className="text-gray-700 leading-relaxed">
                  La collera di Achille è il motore narrativo del poema. Offeso da Agamennone, l'eroe si ritira dalla battaglia, causando la morte di molti compagni. Solo la morte dell'amico Patroclo lo riporterà in guerra, assetato di vendetta contro Ettore.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-trophy-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Gloria e Onore</h3>
                <p className="text-gray-700 leading-relaxed">
                  Il concetto di "kleos" (gloria immortale) è centrale nella cultura eroica greca. I guerrieri combattono non solo per la vittoria, ma per essere ricordati nei secoli. Achille sceglie consapevolmente una vita breve ma gloriosa.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-8">
                <div className="w-14 h-14 bg-amber-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-heart-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Umanità e Compassione</h3>
                <p className="text-gray-700 leading-relaxed">
                  Nonostante la violenza della guerra, l'Iliade mostra momenti di profonda umanità: l'addio di Ettore alla moglie Andromaca, il pianto di Priamo per il figlio, la pietà finale di Achille verso il vecchio re troiano.
                </p>
              </div>

              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8">
                <div className="w-14 h-14 bg-violet-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-scales-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Destino e Libero Arbitrio</h3>
                <p className="text-gray-700 leading-relaxed">
                  Gli eroi conoscono spesso il loro destino, ma scelgono comunque di agire. Gli dei intervengono negli affari umani, ma gli uomini mantengono la responsabilità delle loro azioni. Questo equilibrio tra fato e scelta è un tema ricorrente.
                </p>
              </div>
            </div>
          </div>

          {/* Value Section */}
          <div className="bg-gray-900 rounded-3xl p-12 text-white">
            <h2 className="text-4xl font-bold mb-6">Il Valore dell'Opera</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-3 text-orange-400">Letterario</h3>
                <p className="text-gray-300 leading-relaxed">
                  L'Iliade ha stabilito i canoni della narrazione epica: struttura narrativa complessa, caratterizzazione psicologica dei personaggi, uso di similitudini elaborate, linguaggio formulare. Ha influenzato tutta la letteratura successiva, da Virgilio a Dante, fino ai romanzi moderni.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3 text-orange-400">Storico</h3>
                <p className="text-gray-300 leading-relaxed">
                  Sebbene sia un'opera di finzione, l'Iliade offre preziose informazioni sulla società greca arcaica: valori, costumi, organizzazione sociale, pratiche religiose e belliche. Gli scavi archeologici a Troia hanno confermato l'esistenza storica della città.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3 text-orange-400">Culturale</h3>
                <p className="text-gray-300 leading-relaxed">
                  Per i Greci antichi, l'Iliade era molto più di un testo letterario: era un testo educativo fondamentale, che trasmetteva valori, modelli di comportamento e identità culturale. Veniva studiato nelle scuole e recitato nelle feste pubbliche.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3 text-orange-400">Universale</h3>
                <p className="text-gray-300 leading-relaxed">
                  I temi dell'Iliade - guerra, morte, amicizia, vendetta, compassione - sono universali e senza tempo. Il poema continua a parlare ai lettori moderni perché affronta questioni fondamentali dell'esistenza umana con profondità e sensibilità straordinarie.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="whitespace-nowrap">Torna alla Home</span>
            </Link>
            <Link to="/contesto-storico" className="flex items-center gap-2 text-orange-600 hover:text-orange-700 transition-colors font-semibold cursor-pointer">
              <span className="whitespace-nowrap">Contesto Storico-Culturale</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
